import React, { useEffect, useState } from 'react';
import Cookies from 'js-cookie';
import { useSelector } from 'react-redux';
import { FormattedMessage, useIntl } from 'react-intl';
import './PaymentStep.scss';
import { startPayUPayment } from '../../../utils/startPayUPayment';
import { SHIPPING_METHODS, computeTotals, formatPLN, getShippingMethod } from '../../../config/checkout';

// BLIK, cards and Google Pay are all paid through PayU's hosted page — the
// chips only tell the shopper what PayU offers.
const PAYU_CHIPS = [
    { id: 'blik', label: 'BLIK' },
    { id: 'visa', logo: '/img/logos/visa.svg', label: 'Visa' },
    { id: 'mastercard', logo: '/img/logos/mastercard.svg', label: 'Mastercard' },
    { id: 'googlepay', logo: '/img/logos/googlepay.svg', label: 'Google Pay' },
];

const OptionCard = ({ selected, onSelect, name, value, children }) => (
    <label className={`option-card ${selected ? 'selected' : ''}`}>
        <input
            type="radio"
            name={name}
            value={value}
            checked={selected}
            onChange={onSelect}
            className="sr-only"
        />
        {children}
        <span className="checkmark" aria-hidden="true">✓</span>
    </label>
);

const PaymentStep = ({
    shippingData,
    paymentData,
    setPaymentData,
    onBack,
    onOrderCreated,
}) => {
    const intl = useIntl();
    const basketProducts = useSelector((state) => state.cart.products).filter((item) => !item.unavailable);
    const discount = useSelector((state) => state.cart.discount);
    // Sent with the order so emails and the PayU page use the shopper's language.
    const locale = useSelector((state) => state.language.locale);

    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [selectedShipping, setSelectedShipping] = useState(
        getShippingMethod(paymentData?.selectedShipping) ? paymentData.selectedShipping : 'dhl_courier'
    );
    const [selectedPayment, setSelectedPayment] = useState(
        ['payu', 'bank_transfer'].includes(paymentData?.selectedPayment) ? paymentData.selectedPayment : 'payu'
    );

    const method = getShippingMethod(selectedShipping);
    // Cash on delivery decides the payment method by itself.
    const paymentMethod = method?.paymentMethod || selectedPayment;
    const totals = computeTotals(basketProducts, discount, selectedShipping);

    useEffect(() => {
        setPaymentData?.((prev) => ({ ...prev, selectedShipping, selectedPayment }));
    }, [selectedShipping, selectedPayment, setPaymentData]);

    const describeError = (err) => {
        if (err.status === 409 && err.outOfStock?.length) {
            return intl.formatMessage(
                { id: 'checkout_out_of_stock' },
                { products: err.outOfStock.map((p) => p.title).join(', ') }
            );
        }
        if (err.status === 400) return intl.formatMessage({ id: 'checkout_invalid_data' });
        return intl.formatMessage({ id: 'checkout_order_failed' });
    };

    const createOfflineOrder = async () => {
        const token = Cookies.get('token');
        const response = await fetch(`${process.env.REACT_APP_API_URL}/orders`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                ...(token && { Authorization: `Bearer ${token}` }),
            },
            body: JSON.stringify({
                data: {
                    currentStatus: 'pending',
                    items: basketProducts.map((item) => ({
                        id: item.id,
                        documentId: item.documentId,
                        quantity: item.quantity,
                    })),
                    shippingMethod: selectedShipping,
                    shippingData,
                    paymentMethod,
                    discountCode: discount?.code,
                    orderDate: new Date().toISOString(),
                    language: locale,
                },
            }),
        });

        const json = await response.json().catch(() => null);
        if (!response.ok) {
            const err = new Error(json?.error?.message || 'Order failed');
            err.status = response.status;
            err.outOfStock = json?.error?.details?.outOfStock;
            throw err;
        }
        return json?.data;
    };

    const handleCreateOrder = async () => {
        if (!basketProducts?.length) return;
        setLoading(true);
        setError(null);

        try {
            if (paymentMethod === 'payu') {
                await startPayUPayment({
                    cartItems: basketProducts,
                    shippingData,
                    shippingMethod: selectedShipping,
                    discountCode: discount?.code,
                    language: locale,
                });
                return; // the browser is leaving for PayU
            }

            const order = await createOfflineOrder();
            if (!order?.id) throw new Error('Order failed');
            onOrderCreated?.(order);
        } catch (err) {
            console.error('Order creation error:', err);
            setError(describeError(err));
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="payment-step-container">
            <section className="step-section">
                <h2 className="step-title"><FormattedMessage id="checkout_choose_shipping" /></h2>

                <div className="options-grid" role="radiogroup">
                    {SHIPPING_METHODS.map((option) => (
                        <OptionCard
                            key={option.id}
                            name="shipping"
                            value={option.id}
                            selected={selectedShipping === option.id}
                            onSelect={() => setSelectedShipping(option.id)}
                        >
                            <span className="logos">
                                <img src={option.logo} alt="DHL" className="option-logo" />
                            </span>
                            <span className="option-content">
                                <span className="option-title">
                                    <FormattedMessage id={`shipping_${option.id}_title`} />
                                </span>
                                <span className="option-desc">
                                    <FormattedMessage id={`shipping_${option.id}_desc`} />
                                </span>
                                <span className="option-price">
                                    <span className="price-value">
                                        {totals.freeShipping
                                            ? <FormattedMessage id="shipping_free" />
                                            : formatPLN(option.price)}
                                    </span>
                                </span>
                            </span>
                        </OptionCard>
                    ))}
                </div>
            </section>

            <section className="step-section">
                <h2 className="step-title"><FormattedMessage id="checkout_choose_payment" /></h2>

                {method?.paymentMethod === 'cod' ? (
                    <p className="cod-note"><FormattedMessage id="payment_cod_note" /></p>
                ) : (
                    <div className="options-grid" role="radiogroup">
                        <OptionCard
                            name="payment"
                            value="payu"
                            selected={selectedPayment === 'payu'}
                            onSelect={() => setSelectedPayment('payu')}
                        >
                            <span className="logos">
                                <img src="/img/logos/payu.svg" alt="PayU" className="option-logo" />
                            </span>
                            <span className="option-content">
                                <span className="option-title"><FormattedMessage id="payment_payu_title" /></span>
                                <span className="option-desc"><FormattedMessage id="payment_payu_desc" /></span>
                                <span className="payment-chips">
                                    {PAYU_CHIPS.map((chip) => (
                                        <span className="payment-chip" key={chip.id} title={chip.label}>
                                            {chip.logo ? <img src={chip.logo} alt={chip.label} /> : chip.label}
                                        </span>
                                    ))}
                                </span>
                            </span>
                        </OptionCard>

                        <OptionCard
                            name="payment"
                            value="bank_transfer"
                            selected={selectedPayment === 'bank_transfer'}
                            onSelect={() => setSelectedPayment('bank_transfer')}
                        >
                            <span className="option-content">
                                <span className="option-title"><FormattedMessage id="payment_bank_transfer_title" /></span>
                                <span className="option-desc"><FormattedMessage id="payment_bank_transfer_desc" /></span>
                            </span>
                        </OptionCard>
                    </div>
                )}
            </section>

            {error && (
                <div className="alert alert-error" role="alert">
                    {error}
                </div>
            )}

            <div className="step-actions">
                <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={onBack}
                    disabled={loading}
                >
                    ← <FormattedMessage id="back" />
                </button>

                <button
                    type="button"
                    className="btn btn-primary"
                    onClick={handleCreateOrder}
                    disabled={loading || !basketProducts.length}
                >
                    {loading
                        ? <FormattedMessage id="processing" />
                        : <FormattedMessage
                            id={paymentMethod === 'payu' ? 'place_order_and_pay' : 'place_order_with_total'}
                            values={{ total: formatPLN(totals.total) }}
                        />}
                </button>
            </div>
        </div>
    );
};

export default PaymentStep;
